/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { FileText, Plus, Trash2, Edit, Globe, Lock } from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import Spinner from '@/components/ui/Spinner'
import { formatDate } from '@/lib/utils'

interface TemplateRow {
  id: string
  name: string
  campaign_id: string | null
  created_at: string
}

type TemplateTab = 'campaign' | 'shared'

/**
 * When campaignId is provided: shows this campaign's templates + shared tab.
 * When campaignId is null: shows only shared (global) templates — used by /templates.
 */
export default function TemplatesContent({ campaignId }: { campaignId: string | null }) {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<TemplateTab>(campaignId ? 'campaign' : 'shared')

  const { data: allTemplates, isLoading } = useQuery({
    queryKey: ['templates', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('templates')
        .select('id, name, campaign_id, created_at')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: true })
      return (data ?? []) as TemplateRow[]
    },
    enabled: !!user,
  })

  const campaignTemplates = (allTemplates ?? []).filter(t => t.campaign_id === campaignId)
  const sharedTemplates   = (allTemplates ?? []).filter(t => t.campaign_id === null)
  const displayed         = activeTab === 'campaign' ? campaignTemplates : sharedTemplates

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('templates').delete().eq('id', id).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  })

  // Edit route: campaign templates → /campaigns/:id/templates/:tid/edit
  //             shared templates  → /templates/:tid/edit
  function editRoute(t: TemplateRow) {
    if (t.campaign_id) return `/campaigns/${t.campaign_id}/templates/${t.id}/edit`
    return `/templates/${t.id}/edit`
  }

  // New template route
  const newRoute = campaignId && activeTab === 'campaign'
    ? `/campaigns/${campaignId}/templates/new`
    : '/templates/new'

  const ICONS = ['📄', '📨', '✨', '📰', '🎯', '💡', '🚀', '📊']

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        {campaignId ? (
          <div className="flex gap-1">
            {([
              ['campaign', <Lock className="w-3.5 h-3.5" />,  'This campaign'] ,
              ['shared',   <Globe className="w-3.5 h-3.5" />, 'Shared']        ,
            ] as const).map(([t, icon, label]) => (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === t ? 'bg-brand-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {icon}{label}
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === t ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {t === 'campaign' ? campaignTemplates.length : sharedTemplates.length}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 flex items-center gap-1.5">
            <Globe className="w-4 h-4" /> Shared templates — available to all campaigns
          </p>
        )}

        <div className="flex gap-2 shrink-0">
          <Link to={newRoute} className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> New template
          </Link>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : !displayed.length ? (
        <EmptyState
          icon={FileText}
          title={activeTab === 'campaign' ? 'No campaign templates yet' : 'No shared templates yet'}
          description={
            activeTab === 'campaign'
              ? 'Create a template specific to this campaign, or switch to Shared to use global ones.'
              : 'Shared templates are available to all campaigns.'
          }
          action={<Link to={newRoute} className="btn-primary">New template</Link>}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayed.map((t, i) => (
            <div key={t.id} className="card p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center text-2xl select-none">
                  {ICONS[i % ICONS.length]}
                </div>
                {t.campaign_id === null && (
                  <span className="flex items-center gap-1 text-[10px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">
                    <Globe className="w-2.5 h-2.5" /> Shared
                  </span>
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{t.name}</p>
                <p className="text-xs text-gray-400 mt-1">{formatDate(t.created_at)}</p>
              </div>
              <div className="flex gap-2">
                <Link
                  to={editRoute(t)}
                  className="btn-secondary flex-1 justify-center text-xs py-1.5"
                >
                  <Edit className="w-3.5 h-3.5" /> Edit in builder
                </Link>
                <button
                  onClick={() => confirm('Delete this template?') && del.mutate(t.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 rounded border border-gray-200 hover:border-red-200"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
